import { AdUnitEnv, PbsAdUnit } from '@pubstack/common/src/pbs/adunit'
import { FunctionComponent, useState } from 'react'
import { useAdUnitsWithPagination, useUpdateAdUnit } from '~/api/pbs/ad-units.api'
import { ActionButtons, ActionsTableCell, NoResultsTableCell } from '~/components/_styled/table'
import Button from '~/components/Button'
import { IllustrationMessage } from '~/components/IllustrationMessage'
import { Pagination } from '~/components/Pagination'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import Table, { TableColumns } from '~/components/table/Table'
import TableCell from '~/components/table/TableCell'
import TableRow from '~/components/table/TableRow'
import { Tooltip } from '~/components/Tooltip'
import { UnarchiveConfirmationModal } from '~/feature-components/UnarchiveConfirmationModal'

const COLUMNS: TableColumns<PbsAdUnit> = [
  { name: 'Ad unit', isSortable: false },
  { name: 'Actions', isSortable: false },
]

/**
 * One archived Node ad unit, and the way back.
 *
 * The row is rendered `archived`, which is what the table already uses to grey a row out and stop it
 * being clickable — an archived ad unit has no flyout worth opening, since it is in no stack.
 */
const ArchivedAdUnitTr: FunctionComponent<{ adUnit: PbsAdUnit }> = ({ adUnit }) => {
  const modal = useGlobalModal()
  const { mutateAsync: updateAdUnit, isPending } = useUpdateAdUnit(adUnit.id, {
    successToast: (
      <>
        <strong>{adUnit.name}</strong> was restored.
      </>
    ),
    errorToast: (
      <>
        An error occurred while trying to restore <strong>{adUnit.name}</strong>.
      </>
    ),
  })

  const handleUnarchive = async () => {
    if (isPending) {
      return
    }
    await updateAdUnit({ ...adUnit, enabled: true })
  }

  return (
    <TableRow archived>
      <TableCell>{adUnit.name}</TableCell>
      <ActionsTableCell>
        <ActionButtons>
          <Tooltip title={'Unarchive'}>
            <Button
              iconName={isPending ? 'pubstack' : 'unarchive'}
              iconSpin={isPending}
              disabled={isPending}
              variant={'tertiary'}
              title={'Unarchive'}
              onClick={() =>
                modal.open(UnarchiveConfirmationModal, {
                  name: adUnit.name,
                  noun: 'ad unit',
                  consequence: 'It will return to the ad unit list and be served again by every stack that carries it.',
                  onUnarchive: handleUnarchive,
                  isPending,
                  trackingPrefix: 'common-archived-ad-units-unarchive',
                })
              }
              trackingLabel={'common-archived-ad-units-unarchive'}
            />
          </Tooltip>
        </ActionButtons>
      </ActionsTableCell>
    </TableRow>
  )
}

type Props = {
  env: Exclude<AdUnitEnv, 'web'>
  search: string
}

const ITEMS_PER_PAGE = 25

/** The archived ad units of one Node environment. The page chrome is the shell's; this is the table. */
export const ArchivedPbsAdUnitsTable: FunctionComponent<Props> = ({ env, search }) => {
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState<number>(ITEMS_PER_PAGE)

  // A new search is a new list: page 4 of the previous one is not a place the reader asked to be. Adjusted
  // while rendering rather than in an effect, so the first paint already shows page 1 instead of flashing a
  // page that no longer exists — see "You Might Not Need an Effect".
  const [searchOnPage, setSearchOnPage] = useState(search)
  if (searchOnPage !== search) {
    setSearchOnPage(search)
    setPage(1)
  }

  // `archived` asks the list endpoint for the retired ones; it used to fetch every ad unit and sieve here.
  const {
    data: { data: adUnits, pagination },
    isLoading,
  } = useAdUnitsWithPagination({ filters: { search: search || undefined, env, archived: true }, pagination: { page, limit } })

  // Without a search the total is the whole archive, so an empty one is genuinely empty rather than filtered.
  if (!isLoading && pagination.total === 0 && !search) {
    return <IllustrationMessage iconName={'night_sky'} iconSize={'190px'} title={''} message={'No archived ad unit.'} />
  }

  return (
    <>
      <Table columns={COLUMNS} isLoading={isLoading}>
        {adUnits.length > 0 ? (
          adUnits.map((adUnit) => <ArchivedAdUnitTr key={adUnit.id} adUnit={adUnit} />)
        ) : (
          <TableRow>
            <NoResultsTableCell colspan={COLUMNS.length}>No results</NoResultsTableCell>
          </TableRow>
        )}
      </Table>

      <Pagination
        currentPage={page}
        totalPages={pagination.totalPages}
        limit={limit}
        totalItems={pagination.total}
        onPageChange={setPage}
        onLimitChange={(l) => {
          setLimit(l)
          setPage(1)
        }}
      />
    </>
  )
}
